// -----------------------------------------------------------------------------
//
// This file is the copyrighted property of Tableau Software and is protected
// by registered patents and other applicable U.S. and international laws and
// regulations.
//
// Unlicensed use of the contents of this file is prohibited. Please refer to
// the NOTICES.txt file for further details.
//
// -----------------------------------------------------------------------------
/// <reference path='../../../ts/libs.ts' />
import { ButtonType, ButtonWidget, FlyoutListMenuWidget, HorizontalOriginOption,
  Img, MenuItemInfo, VerticalOriginOption } from '@tableau/widgets';
import * as React from 'react';
import ReactDOM = require('react-dom');
import { connect, Dispatch } from 'react-redux';
import { Messages } from '../../../codegen/Localize';
import { ExtractEncryptionState } from '../../../ts/models/IDataSourceInfo';
import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { DataSources } from '../../../content/resources/Datasources';
import { IDatasourceActions } from '../../../ts/ServerApi/types/IDatasourceActions';
import { IWorkbookActions } from '../../../ts/ServerApi/types/IWorkbookActions';
import { changeExtractDsToLive } from '../../actions/spaces/ChangeExtractDsToLiveAction';
import { createExtractsMethod } from '../../actions/spaces/RunCreateExtractsAction';
import { AppState } from '../../state/AppState';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';
import { ExtractEncryptionMode } from '../../../sites/SitePropertyTypes';

export class DataSourceActionDropdown extends React.PureComponent<IDataSourceActionDropdownProps, IDataSourceActionDropdownState> {
  private buttonEl: HTMLElement | undefined;
  static changeExtractToLive = 'changeExtractToLive';
  static createExtract = 'createExtract';
  static createEncryptedExtract = 'createEncryptedExtract';
  static createUnencryptedExtract = 'createUnencryptedExtract';

  constructor(props: IDataSourceActionDropdownProps) {
    super(props);
    this.state = {
      inProgress: false,
      isMenuOpen: false
    };
    this.toggleMenuOpen = this.toggleMenuOpen.bind(this);
    this.close = this.close.bind(this);
    this.handleButtonRef = this.handleButtonRef.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
  }

  toggleMenuOpen() {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  }

  handleButtonRef(ref: ButtonWidget) {
    this.buttonEl = ref ? ReactDOM.findDOMNode(ref) as HTMLElement : undefined;
  }

  handleSelect(value: string) {
    switch (value) {
      case DataSourceActionDropdown.changeExtractToLive:
        this.props.dispatchChangeExtractToLiveAction(this.props.itemInfo);
        break;
      case DataSourceActionDropdown.createExtract:
        this.props.dispatchCreateExtractAction(this.props.itemInfo, this.props.siteEARMode === ExtractEncryptionMode.ENABLED ? ExtractEncryptionState.encrypted: ExtractEncryptionState.unencrypted);
        break;
      case DataSourceActionDropdown.createEncryptedExtract:
        this.props.dispatchCreateExtractAction(this.props.itemInfo, ExtractEncryptionState.encrypted);
        break;
      case DataSourceActionDropdown.createUnencryptedExtract:
        this.props.dispatchCreateExtractAction(this.props.itemInfo, ExtractEncryptionState.unencrypted);
        break;
      default:
         throw 'Action ' +value+ ' does\'t supprot';
    }
    this.close();
  }

  close(): void  {
    this.setState({ isMenuOpen: false });
  }

  public render(): React.ReactNode {

    const actionMenuItem = {
      LIVE: {
        value: DataSourceActionDropdown.changeExtractToLive,
        displayValue: Messages.changeExtractDsToLiveAction(),
        disabled: false,
      },
      EXTRACT: {
        value: DataSourceActionDropdown.createExtract,
        displayValue: Messages.createExtractForDsAction()
      },
      EXTRACT_ENCRYPT: {
        value: DataSourceActionDropdown.createEncryptedExtract,
        displayValue: Messages.createExtractsForDsActionEncrypted()
      },
      EXTRACT_UNENCRYPT:{
        value: DataSourceActionDropdown.createUnencryptedExtract,
        displayValue: Messages.createExtractsForDsActionUnencrypted()
      }
    };

    const hasExtracts = this.props.itemInfo.hasExtracts || this.props.itemInfo.hasEncryptableExtracts;

    const { extractEncryptionState, extractCreationPending, isExtractOnly, isFileBased} =  this.props.itemInfo;
    const availableActions = this.props.availableActions;

    let menuItemInfos: MenuItemInfo[] = [{...actionMenuItem.LIVE, checked: !hasExtracts, disabled: !availableActions.extractsToLive || isFileBased || isExtractOnly || !hasExtracts || extractCreationPending}];

    if (!this.props.encryptionEnabled || this.props.siteEARMode === ExtractEncryptionMode.DISABLED) {
      menuItemInfos.push({...actionMenuItem.EXTRACT, checked: hasExtracts, disabled: !availableActions.createExtracts || hasExtracts || extractCreationPending});
    } else if (this.props.siteEARMode === ExtractEncryptionMode.ENFORCED) {
      if(hasExtracts && extractEncryptionState === ExtractEncryptionState.encrypted) {
        menuItemInfos.push({...actionMenuItem.EXTRACT_ENCRYPT, checked: hasExtracts, disabled: !availableActions.createExtracts || hasExtracts|| extractCreationPending});
      } else if(hasExtracts && extractEncryptionState === ExtractEncryptionState.unencrypted) {
        menuItemInfos.push({...actionMenuItem.EXTRACT_UNENCRYPT, checked: hasExtracts, disabled: !availableActions.createExtracts || hasExtracts|| extractCreationPending});
      } else {
        menuItemInfos.push({...actionMenuItem.EXTRACT_ENCRYPT, checked: hasExtracts, disabled: !availableActions.createExtracts || hasExtracts || extractCreationPending});
      }
    } else {
      let subMenuItemInfos = [];
      subMenuItemInfos.push({...actionMenuItem.EXTRACT_ENCRYPT,
          checked: hasExtracts && extractEncryptionState === ExtractEncryptionState.encrypted,
          disabled:  extractCreationPending || (hasExtracts && extractEncryptionState === ExtractEncryptionState.encrypted),
          displayValue: Messages.createExtractsForDsSubActionEncrypted()});
      subMenuItemInfos.push({...actionMenuItem.EXTRACT_UNENCRYPT,
         checked: hasExtracts && extractEncryptionState === ExtractEncryptionState.unencrypted,
         disabled: !availableActions.createExtracts || extractCreationPending || (hasExtracts && extractEncryptionState === ExtractEncryptionState.unencrypted),
         displayValue: Messages.createExtractsForDsSubActionUnencrypted()});
      menuItemInfos.push({
        ...actionMenuItem.EXTRACT,
        disabled: !availableActions.createExtracts,
        displayValue:  hasExtracts? (extractEncryptionState === ExtractEncryptionState.encrypted ?
          Messages.createExtractsForDsActionEncrypted() : Messages.createExtractsForDsActionUnencrypted()) : Messages.createExtractForDsAction(),
        menuItemInfos: subMenuItemInfos,
        checked: hasExtracts
      });
    }

    const buttonStyles: React.CSSProperties = {
      cursor: 'pointer'
    };
    // we will have to build new wigets to support IconDropdownArrowSvg, then remove below code.
    // TFSID: 921912
    // TFSID: 950792 for the UT
    const IconDropdownArrowSvg: string =
      `<svg xmlns="http://www.w3.org/2000/svg" class="$state$" width="7" height="5">
        <style>
          .onDarkBackground{opacity:0.5}
          .onDarkBackground.active,.onDarkBackground.hover{opacity:1}
          .disabled{opacity:0.35!important}
          .active path,.hover path{fill:#333}
          path,.disabled path{fill:#666}
          .onDarkBackground path{fill:#fff!important}
        </style>
        <path d="M5.8,1H1.2C1.1,1,1,1.1,1.1,1.2l2.3,2.7C3.4,4,3.6,4,3.6,3.9l2.3-2.7 C6,1.1,5.9,1,5.8,1z"/>
      </svg>`;
    const svgDataUriRegExp: RegExp = /^data\:image\/svg\+xml/;
    function encodeSvg(svg: string): string {
      return svgDataUriRegExp.test(svg) ? svg : `data:image/svg+xml;base64,${btoa(svg)}`;
    }
    const encryptionStatusSpan: React.ReactElement = <span  className={SpaceHeaderStyles.ExtractStatusText}><span key={2} className={`disabled`}>
            {DataSources.DatasourceStatus(this.props.encryptionEnabled, this.props.itemInfo.connectionType,this.props.itemInfo.isFileBased,this.props.itemInfo.hasExtracts, this.props.itemInfo.extractEncryptionState,
              this.props.itemInfo.extractCreationPending, this.props.siteEARMode)}</span></span>;
    /* eslint-disable jsx-a11y/anchor-is-valid */
    const encryptionStatusAnchor: React.ReactElement = <span  className={SpaceHeaderStyles.ExtractStatusText}><a key={2} className={SpaceHeaderStyles.headerLink}>
              {DataSources.DatasourceStatus(this.props.encryptionEnabled, this.props.itemInfo.connectionType,this.props.itemInfo.isFileBased,this.props.itemInfo.hasExtracts, this.props.itemInfo.extractEncryptionState,
                this.props.itemInfo.extractCreationPending, this.props.siteEARMode)}</a></span>;

    return (
      <React.Fragment>
        {this.props.disabled ?
          (this.props.itemInfo.hasExtracts ? encryptionStatusSpan:null):
          (<ButtonWidget
          buttonType={ButtonType.IconOutline}
          handleClick={this.toggleMenuOpen}
          ref={this.handleButtonRef}
          testId="data-source-action-menu"
          style={buttonStyles}
          title={Messages.userMenuButton()}
          >
            {encryptionStatusAnchor}
            <span className={SpaceHeaderStyles.DataStatusIcon}>
              <Img src={encodeSvg(IconDropdownArrowSvg)} style={{ margin: this.props.children ? undefined : `0 auto`, width: 14, height:10 }} />
            </span>
            {this.state.isMenuOpen &&
              <FlyoutListMenuWidget
                menuStyle={{fontWeight: 'normal', minWidth:150, maxWidth: 170}}
                allowAutoPosition
                anchorEl={this.buttonEl}
                anchorOrigin={{
                  vertical: VerticalOriginOption.bottom,
                  horizontal: HorizontalOriginOption.left,
                }}
                menuItemInfos={menuItemInfos}
                onSelect={this.handleSelect}
                requestCloseAll={this.close}
                testId="data-action-menu"
                toggleEl={this.buttonEl}
              />
            }
        </ButtonWidget>)
        }
     </React.Fragment>
    );
  }
}

type IDataSourceActionDropdownState = {
  isMenuOpen: boolean;
  inProgress: boolean;
};

type IDataSourceActionDropdownOwnProps= {
  itemInfo: ISpaceItemDetailInfo<'datasource'>;
  availableActions: IWorkbookActions | IDatasourceActions;
  disabled: boolean;
  encryptionEnabled: boolean;
  siteEARMode: ExtractEncryptionMode;
};

type IDataSourceActionDropdownDispatchProps = {
  dispatchCreateExtractAction?: (itemInfo: ISpaceItemDetailInfo<'datasource'>, encryptionState: ExtractEncryptionState) => void;
  dispatchChangeExtractToLiveAction?: (itemInfo: ISpaceItemDetailInfo<'datasource'>) => void;
};

export type IDataSourceActionDropdownProps = IDataSourceActionDropdownOwnProps & IDataSourceActionDropdownDispatchProps;

function mapDispatchToProps(dispatch: Dispatch<AppState>): IDataSourceActionDropdownDispatchProps {
  return {
    dispatchCreateExtractAction: (itemInfo: ISpaceItemDetailInfo<'datasource'>, encryptionState: ExtractEncryptionState) => dispatch(createExtractsMethod([itemInfo], encryptionState)),
    dispatchChangeExtractToLiveAction: (itemInfo: ISpaceItemDetailInfo<'datasource'>) => dispatch(changeExtractDsToLive([itemInfo]))
  };
}

export const ConnectedDataSourceActionDropdown = connect(null, mapDispatchToProps)(DataSourceActionDropdown);
